/**
 * ============================================================
 * ELOISE ACCESSOIRES — theme.js
 * Core JS du thème — point d'entrée principal
 *
 * Responsabilités :
 *   - Initialisation globale au chargement
 *   - Header scroll state (transparent → scrolled)
 *   - Animation hero (séquence d'entrée)
 *   - Menu mobile (burger)
 *   - Chargement conditionnel des modules sociaux
 *   - Event delegation globale
 *
 * Architecture :
 *   - Vanilla ES6+ uniquement (modules natifs)
 *   - Aucun framework, aucune dépendance
 *   - type="module" dans theme.liquid (defer natif)
 *
 * Convention :
 *   - Fonctions avec préfixe descriptif (initX, handleX, onX)
 *   - Pas de variables globales — tout dans le module
 *   - Comments inline sur la logique non-évidente uniquement
 * ============================================================
 */

'use strict';

// ============================================================
// CONSTANTES ET CONFIGURATION
// ============================================================

const CONFIG = {
  headerScrollThreshold: 20,          // px avant que le header passe en mode scrolled
  heroAnimationDelay:    150,          // ms après DOMContentLoaded pour déclencher l'animation
  scrollHintHideAt:      80,           // px de scroll avant de cacher le hint
  socialEmbedThreshold:  0.3,          // fraction visible pour charger les embeds sociaux
};

const SELECTORS = {
  header:          '.site-header',
  headerBurger:    '.site-header__burger',
  headerNav:       '.site-header__nav',
  heroSection:     '[data-section-type="hero-immersive"]',
  heroParallax:    '[data-hero-parallax]',
  heroContent:     '[data-hero-content]',
  scrollHint:      '.hero-immersive__scroll-hint',
  socialEmbeds:    '[data-social-embed]',
  accordions:      '.accordion__trigger',
  toast:           '.toast',
};

const CLASSES = {
  headerScrolled:  'site-header--scrolled',
  heroAnimated:    'hero-animated',
  menuOpen:        'menu-open',
  toastVisible:    'toast--visible',
};


// ============================================================
// INIT — Point d'entrée
// ============================================================

document.addEventListener('DOMContentLoaded', () => {

  initHeader();
  initHeroAnimation();
  initMobileMenu();
  initSearch();
  initAccordions();

  // Délai minimal pour que le premier paint soit terminé
  requestAnimationFrame(() => {
    initScrollBehaviors();
    initLazyModules();
  });

});


// ============================================================
// HEADER — État scroll
// Le header commence transparent sur le hero,
// devient sombre+blur dès que le visiteur scroll
// ============================================================

function initHeader() {

  const header = document.querySelector(SELECTORS.header);
  if (!header) return;

  let ticking = false;

  function updateHeaderState() {
    const scrollY = window.scrollY;

    if (scrollY > CONFIG.headerScrollThreshold) {
      header.classList.add(CLASSES.headerScrolled);
    } else {
      header.classList.remove(CLASSES.headerScrolled);
    }

    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(updateHeaderState);
      ticking = true;
    }
  }, { passive: true });

  // Vérification initiale (si page chargée après un scroll)
  updateHeaderState();
}


// ============================================================
// ANIMATION HERO — Séquence d'entrée
// Ajoute .hero-animated au body pour déclencher les
// animations CSS définies dans animations.css
// Timing : légèrement après DOMContentLoaded pour que le
// premier paint soit visible avant le début de l'animation
// ============================================================

function initHeroAnimation() {

  const hero = document.querySelector(SELECTORS.heroSection);
  if (!hero) return;

  // Vérification prefers-reduced-motion
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  if (prefersReduced) {
    // Apparition directe sans animation
    document.body.classList.add(CLASSES.heroAnimated);
    return;
  }

  // Délai minimal pour le premier paint
  setTimeout(() => {
    document.body.classList.add(CLASSES.heroAnimated);
  }, CONFIG.heroAnimationDelay);

  // Masquer le hint de scroll après un premier scroll
  const scrollHint = hero.querySelector(SELECTORS.scrollHint);
  if (scrollHint) {
    window.addEventListener('scroll', () => {
      if (window.scrollY > CONFIG.scrollHintHideAt) {
        scrollHint.style.opacity = '0';
        scrollHint.style.pointerEvents = 'none';
      }
    }, { passive: true, once: true });
  }
}


// ============================================================
// PARALLAXE HERO
// Transform translateY uniquement — GPU exclusif
// Désactivé automatiquement sur mobile (via CSS will-change:auto)
// ============================================================

function initScrollBehaviors() {

  const heroParallax = document.querySelector(SELECTORS.heroParallax);
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const isMobile = window.matchMedia('(max-width: 768px)').matches;

  // Parallaxe uniquement sur desktop et sans prefers-reduced-motion
  if (heroParallax && !prefersReduced && !isMobile) {
    initHeroParallax(heroParallax);
  }

  // Masquage du scroll hint
  initScrollHint();
}

function initHeroParallax(element) {

  let ticking = false;
  const heroHeight = element.closest('.hero-immersive')?.offsetHeight || window.innerHeight;

  function updateParallax() {
    const scrollY = window.scrollY;

    // Le parallaxe ne s'applique que pendant le hero
    if (scrollY <= heroHeight) {
      // Fond média : 85% de la vitesse de scroll (légèrement plus lent = profondeur)
      const parallaxOffset = scrollY * 0.15;
      element.style.transform = `translateY(${parallaxOffset}px)`;
    }

    ticking = false;
  }

  window.addEventListener('scroll', () => {
    if (!ticking) {
      requestAnimationFrame(updateParallax);
      ticking = true;
    }
  }, { passive: true });
}

function initScrollHint() {
  const scrollHint = document.querySelector(SELECTORS.scrollHint);
  if (!scrollHint) return;

  window.addEventListener('scroll', () => {
    if (window.scrollY > CONFIG.scrollHintHideAt) {
      scrollHint.style.opacity = '0';
    }
  }, { passive: true, once: true });
}


// ============================================================
// MENU MOBILE
// Toggle simple — le drawer complet sera dans interactions.js
// ============================================================

function initMobileMenu() {

  const burger = document.querySelector(SELECTORS.headerBurger);
  const nav    = document.querySelector(SELECTORS.headerNav);
  const header = document.querySelector(SELECTORS.header);
  const drawer = document.getElementById('mobile-nav-drawer');

  if (!burger) return;

  burger.setAttribute('aria-expanded', 'false');
  burger.setAttribute('aria-controls', drawer ? 'mobile-nav-drawer' : 'site-nav');
  burger.setAttribute('aria-label', 'Menu');

  function openMenu() {
    burger.setAttribute('aria-expanded', 'true');
    document.body.classList.add(CLASSES.menuOpen);
    header?.classList.add('site-header--menu-open');
    if (drawer) {
      drawer.removeAttribute('hidden');
      drawer.setAttribute('aria-hidden', 'false');
      // Focus trap : focus sur le premier lien
      requestAnimationFrame(() => {
        const firstLink = drawer.querySelector('a, button');
        firstLink?.focus();
      });
    }
  }

  function closeMenu() {
    burger.setAttribute('aria-expanded', 'false');
    document.body.classList.remove(CLASSES.menuOpen);
    header?.classList.remove('site-header--menu-open');
    if (drawer) {
      drawer.setAttribute('hidden', '');
      drawer.setAttribute('aria-hidden', 'true');
    }
    burger.focus();
  }

  burger.addEventListener('click', () => {
    const isOpen = burger.getAttribute('aria-expanded') === 'true';
    isOpen ? closeMenu() : openMenu();
  });

  // Bouton fermer dans le drawer
  document.addEventListener('click', (e) => {
    const closeBtn = e.target.closest('[data-action="close-menu"]');
    if (closeBtn) closeMenu();
    // Fermer sur clic hors drawer (overlay)
    if (drawer && !drawer.contains(e.target) && !burger.contains(e.target)) {
      if (document.body.classList.contains(CLASSES.menuOpen)) closeMenu();
    }
  });

  // Fermer le menu sur Escape
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && document.body.classList.contains(CLASSES.menuOpen)) {
      closeMenu();
    }
  });
}


// ============================================================
// RECHERCHE — Overlay + Prédiction AJAX
// ============================================================

function initSearch() {
  const searchBtn   = document.querySelector('[data-action="open-search"]');
  const overlay     = document.getElementById('search-overlay');
  if (!searchBtn || !overlay) return;

  const input       = overlay.querySelector('.site-header__search-input');
  const suggestions = document.getElementById('search-suggestions');

  function openSearch() {
    overlay.removeAttribute('hidden');
    overlay.setAttribute('aria-hidden', 'false');
    requestAnimationFrame(() => input?.focus());
  }

  function closeSearch() {
    overlay.setAttribute('hidden', '');
    overlay.setAttribute('aria-hidden', 'true');
    hideSuggestions();
    searchBtn.focus();
  }

  function hideSuggestions() {
    if (!suggestions) return;
    suggestions.setAttribute('hidden', '');
    suggestions.innerHTML = '';
  }

  function escapeHtml(str) {
    return String(str).replace(/[&<>"']/g, c =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c])
    );
  }

  function renderSuggestions(products, query) {
    if (!suggestions) return;
    if (!products.length) { hideSuggestions(); return; }

    const items = products.map(p => {
      const imgSrc  = typeof p.image === 'string' ? p.image : (p.image?.url || '');
      const imgHtml = imgSrc
        ? `<img class="search-suggestion__image" src="${imgSrc}&width=104" alt="${escapeHtml(p.title)}" width="52" height="52" loading="lazy">`
        : `<div class="search-suggestion__image search-suggestion__image--empty"></div>`;
      const priceNum = parseFloat(p.price);
      const price    = isNaN(priceNum) ? '' : new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(priceNum);
      return `<a href="${p.url}" class="search-suggestion" role="option" tabindex="-1">
        ${imgHtml}
        <div class="search-suggestion__info">
          <span class="search-suggestion__title">${escapeHtml(p.title)}</span>
          <span class="search-suggestion__price">${price}</span>
        </div>
      </a>`;
    }).join('');

    const allLink = `<a href="/search?q=${encodeURIComponent(query)}&type=product" class="search-suggestions__all-link">Voir tous les résultats →</a>`;
    suggestions.innerHTML = items + allLink;
    suggestions.removeAttribute('hidden');

    // Navigation clavier dans les suggestions
    suggestions.querySelectorAll('.search-suggestion').forEach((item, idx, all) => {
      item.setAttribute('tabindex', '-1');
      item.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); all[idx + 1]?.focus(); }
        if (e.key === 'ArrowUp')   { e.preventDefault(); idx === 0 ? input?.focus() : all[idx - 1]?.focus(); }
        if (e.key === 'Escape')    { closeSearch(); }
      });
    });
  }

  // Prédiction AJAX avec debounce 300 ms
  let abortController = null;

  const fetchSuggestions = window.EloiseTheme?.debounce(async (query) => {
    if (query.length < 2) { hideSuggestions(); return; }
    if (abortController) abortController.abort();
    abortController = new AbortController();
    try {
      const url  = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=4&resources[options][unavailable_products]=last`;
      const res  = await fetch(url, { signal: abortController.signal });
      if (!res.ok) return;
      const data = await res.json();
      renderSuggestions(data?.resources?.results?.products || [], query);
    } catch (err) {
      if (err.name !== 'AbortError') console.warn('[ELOISE] Predictive search:', err);
    }
  }, 300);

  if (input && suggestions) {
    input.addEventListener('input', () => fetchSuggestions?.(input.value.trim()));

    // ArrowDown depuis l'input → navigation dans la liste
    input.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowDown' && !suggestions.hidden) {
        e.preventDefault();
        suggestions.querySelector('.search-suggestion')?.focus();
      }
    });
  }

  searchBtn.addEventListener('click', openSearch);

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-action="close-search"]')) { closeSearch(); return; }
    if (!overlay.hasAttribute('hidden') && !overlay.contains(e.target) && !searchBtn.contains(e.target)) {
      closeSearch();
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !overlay.hasAttribute('hidden')) closeSearch();
  });
}


// ============================================================
// CHARGEMENT DIFFÉRÉ DES MODULES SOCIAUX
// Les embeds TikTok/Pinterest/Instagram sont lourds.
// Ils ne chargent que quand l'élément est proche du viewport.
// ============================================================

function initLazyModules() {

  const socialEmbeds = document.querySelectorAll(SELECTORS.socialEmbeds);
  if (!socialEmbeds.length) return;

  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        const el = entry.target;
        const embedType = el.dataset.socialEmbed;

        loadSocialEmbed(embedType, el);
        observer.unobserve(el);
      }
    });
  }, {
    rootMargin: '200px 0px', // Pré-chargement 200px avant visibilité
    threshold: CONFIG.socialEmbedThreshold,
  });

  socialEmbeds.forEach(embed => observer.observe(embed));
}

function loadSocialEmbed(type, container) {
  // Import dynamique du module social (créé plus tard)
  import('./social-embed.js')
    .then(module => module.init(type, container))
    .catch(err => {
      // Silencieux — les embeds sociaux ne sont pas critiques
      console.warn(`[ELOISE] Social embed (${type}) non chargé :`, err);
    });
}


// ============================================================
// TOAST — Système de notification
// Accessible via window.EloiseTheme.toast() depuis n'importe quel module
// ============================================================

function showToast(message, type = 'success', duration = 3500) {

  // Réutiliser un toast existant ou créer un nouveau
  let toast = document.querySelector(SELECTORS.toast);

  if (!toast) {
    toast = document.createElement('div');
    toast.className = 'toast';
    toast.setAttribute('role', 'status');
    toast.setAttribute('aria-live', 'polite');
    document.body.appendChild(toast);
  }

  toast.textContent = message;
  toast.className = `toast toast--${type}`;

  // Afficher
  requestAnimationFrame(() => {
    toast.classList.add(CLASSES.toastVisible);
  });

  // Masquer après délai
  setTimeout(() => {
    toast.classList.remove(CLASSES.toastVisible);
  }, duration);
}


// ============================================================
// ACCORDÉONS GLOBAUX
// Utilisés sur FAQ, descriptions, SEO text
// ============================================================

function initAccordions() {

  document.querySelectorAll(SELECTORS.accordions).forEach(trigger => {

    trigger.setAttribute('aria-expanded', 'false');

    // Trouver le contenu associé
    const content = trigger.nextElementSibling;
    if (!content || !content.classList.contains('accordion__content')) return;

    const contentId = `accordion-${Math.random().toString(36).slice(2, 9)}`;
    content.id = contentId;
    trigger.setAttribute('aria-controls', contentId);

    trigger.addEventListener('click', () => {
      const isOpen = trigger.getAttribute('aria-expanded') === 'true';

      // Fermer les autres accordéons du même groupe
      const parent = trigger.closest('.accordion');
      if (parent) {
        parent.querySelectorAll('.accordion__trigger[aria-expanded="true"]').forEach(other => {
          if (other !== trigger) {
            other.setAttribute('aria-expanded', 'false');
            const otherContent = other.nextElementSibling;
            if (otherContent) otherContent.style.height = '0';
          }
        });
      }

      // Toggle cet accordéon
      trigger.setAttribute('aria-expanded', String(!isOpen));

      if (!isOpen) {
        // Ouvrir : calculer la hauteur naturelle
        const inner = content.querySelector('.accordion__content-inner');
        content.style.height = inner ? `${inner.offsetHeight}px` : 'auto';
      } else {
        // Fermer
        content.style.height = '0';
      }
    });
  });
}

// ============================================================
// API PUBLIQUE — Accessible par les autres modules
// ============================================================

window.EloiseTheme = {
  toast: showToast,
  config: CONFIG,

  // Utilitaire : debounce pour les event listeners lourds
  debounce(fn, delay = 150) {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), delay);
    };
  },

  // Utilitaire : formatter un prix Shopify (centimes → string)
  formatMoney(cents, currency = 'EUR') {
    const amount = (cents / 100).toFixed(2);
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency,
    }).format(amount);
  },
};
